import { MuiChipsInput } from 'mui-chips-input'
import React from 'react'
import styled, { css } from 'styled-components'

import { Iterable, pipe } from '@blade/std-lib'
import { Close2Icon } from '@blade/ui-kit'

import type {
  MuiChipsInputChipProps,
  MuiChipsInputProps,
} from 'mui-chips-input'

type InputChipsProps = MuiChipsInputProps & { unique?: boolean }

const renderChip = (
  _ChipComponent: React.ElementType<MuiChipsInputChipProps>,
  key: React.Key,
  chipProps: MuiChipsInputChipProps,
) => {
  const handleDelete = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation()
    chipProps.onDelete(chipProps.index)
  }

  return (
    <ChipRoot key={key} className={chipProps.className}>
      <span>{chipProps.label}</span>{' '}
      <Close2Icon size={16} onClick={handleDelete} />
    </ChipRoot>
  )
}

export const InputChips: React.FC<InputChipsProps> = ({
  unique,
  onChange,
  ...props
}) => {
  return (
    <Root
      {...props}
      $hideClearAll={props.hideClearAll ?? false}
      onChange={(value) => {
        if (unique) {
          const unique = pipe(value, Iterable.uniq, Iterable.toArray)
          onChange?.(unique)
          return
        }
        onChange?.(value)
      }}
      renderChip={renderChip}
    />
  )
}

const ChipRoot = styled.div`
  color: var(--text-12);
  border-radius: 4px;
  border: 1px solid var(--backgroundA-5);

  text-align: center;
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  display: grid;
  grid-template-columns: 1fr auto;
  place-content: center;
  padding: 1px 1px 1px 6px;
  align-content: center;
  align-items: center;
  gap: 1px;

  > svg {
    cursor: pointer;
    color: var(--text-8);

    font-size: 14px;
    transition: all 0.2s ease;

    &:hover {
      color: var(--text-10);
    }
  }
`

const Root: React.FC<
  MuiChipsInputProps & {
    $hideClearAll: boolean
  }
> = styled(MuiChipsInput)<{
  $hideClearAll: boolean
}>`
  button {
    padding: 2px;
    color: var(--text-10);
    background: transparent;
    transition: all 0.2s ease;

    &:hover {
      color: var(--text-11);
      background: transparent;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
  fieldset {
    display: none;
  }
  .MuiInputBase-root.MuiOutlinedInput-root {
    row-gap: 0px;
    padding: var(--input-chips-padding, 6.5px);
    border-radius: var(--input-chips-border-radius, 10px);

    border: 0.5px solid transparent;
    background: var(--appearanceA-7);
    transition: all 0.2s ease;

    align-items: center;
    ${(x) =>
      x.$hideClearAll
        ? ''
        : css`
            padding-inline-end: 40px;
          `}

    gap: 2px;

    &:focus-within {
      background: var(--appearance-1);
      border: 0.5px solid var(--backgroundA-4);
    }
  }

  input {
    color: var(--text-12);
    font-family: 'Inter';
    font-size: 11px;
    flex: 1 0 30px;
    min-width: 30px;
    padding: 4px 8px;
    grid-column: input;
    font-weight: 600;

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-text-fill-color: var(--text-12);
      -webkit-box-shadow: 0 0 0px 1000px #000 inset;
      transition: background-color 5000s ease-in-out 0s;
    }

    &::placeholder {
      font-size: 10px;
      font-weight: 400;
      line-height: 20px;
      min-height: 26px;

      font-weight: 400;
      line-height: 20px;

      color: var(--text-8) !important;
      opacity: 1;
      font-weight: 400;
    }
  }
`
